import { useTranslation } from "react-i18next";
// src/pages/static/CookiesPolicyPage.jsx - FIXED VERSION
import React from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Settings, Shield, BarChart, Target, Globe, CheckCircle, XCircle, Info, AlertCircle } from 'lucide-react';
const CookiesPolicyPage = () => {
  const { t } = useTranslation();
  const cookieTypes = [{
    icon: Shield,
    name: 'Strictly Necessary Cookies',
    required: true,
    color: 'bg-red-100 text-red-600',
    borderColor: 'border-red-300',
    description: 'These cookies are essential for the Platform to function properly and cannot be disabled. They enable core functionality such as security, network management, and accessibility.',
    examples: ['User authentication and login sessions', 'Shopping cart and order processing', 'Security and fraud prevention', 'Load balancing and platform performance', 'User preferences and settings'],
    retention: 'Session or 1 year',
    canDisable: false
  }, {
    icon: Settings,
    name: 'Functional Cookies',
    required: false,
    color: 'bg-blue-100 text-blue-600',
    borderColor: 'border-blue-300',
    description: 'These cookies enable enhanced functionality and personalization. They may be set by us or by third-party providers whose services we use on our pages.',
    examples: ['Language preferences', 'Region and location settings', 'Customized user interface', 'Video player settings', 'Social media integration features'],
    retention: '1-2 years',
    canDisable: true
  }, {
    icon: BarChart,
    name: 'Performance & Analytics Cookies',
    required: false,
    color: 'bg-purple-100 text-purple-600',
    borderColor: 'border-purple-300',
    description: 'These cookies help us understand how visitors interact with our Platform by collecting and reporting information anonymously. This helps us improve our services.',
    examples: ['Page views and visitor counts', 'Traffic sources and user paths', 'Time spent on pages', 'Error tracking and diagnostics', 'A/B testing and feature optimization'],
    retention: '1-2 years',
    canDisable: true
  }, {
    icon: Target,
    name: 'Advertising & Marketing Cookies',
    required: false,
    color: 'bg-yellow-100 text-yellow-600',
    borderColor: 'border-yellow-300',
    description: 'These cookies are used to deliver advertisements more relevant to you and your interests. They also help measure the effectiveness of advertising campaigns.',
    examples: ['Targeted advertisements', 'Remarketing and retargeting', 'Conversion tracking', 'Ad performance measurement', 'Cross-platform advertising'],
    retention: '1-2 years',
    canDisable: true
  }];
  const thirdPartyCookies = [{
    provider: 'Google Analytics',
    purpose: 'Website traffic analysis and user behavior tracking',
    cookies: ['_ga', '_gid', '_gat'],
    retention: '2 years',
    link: 'https://policies.google.com/privacy'
  }, {
    provider: 'Facebook Pixel',
    purpose: 'Ad targeting and conversion tracking',
    cookies: ['_fbp', 'fr'],
    retention: '90 days - 2 years',
    link: 'https://www.facebook.com/privacy'
  }, {
    provider: 'Google Ads',
    purpose: 'Ad delivery and remarketing',
    cookies: ['IDE', 'test_cookie'],
    retention: '1-2 years',
    link: 'https://policies.google.com/privacy'
  }, {
    provider: 'Hotjar',
    purpose: 'Heatmaps and user session recordings',
    cookies: ['_hjid', '_hjIncludedInSample'],
    retention: '1 year',
    link: 'https://www.hotjar.com/legal/policies/privacy'
  }, {
    provider: 'Paystack',
    purpose: 'Payment processing and fraud prevention',
    cookies: ['__paystack_mid', '__paystack_sid'],
    retention: '1 year',
    link: 'https://paystack.com/privacy'
  }];
  const managementOptions = [{
    icon: Settings,
    title: 'Cookie Settings (Platform)',
    description: 'Use our cookie preference center to manage your cookie choices directly on the Platform.',
    action: 'Access settings in footer or account preferences'
  }, {
    icon: Globe,
    title: 'Browser Settings',
    description: 'Configure your web browser to block or delete cookies. Most browsers allow you to control cookies through settings.',
    action: 'Check your browser\'s help menu for instructions'
  }, {
    icon: XCircle,
    title: 'Opt-Out Tools',
    description: 'Use industry opt-out tools to prevent advertising cookies from multiple providers.',
    action: 'Visit optout.aboutads.info or youronlinechoices.eu'
  }, {
    icon: Shield,
    title: 'Do Not Track',
    description: 'Enable "Do Not Track" in your browser settings to signal your preference to websites.',
    action: 'Note: Not all websites honor Do Not Track requests'
  }];
  const browserGuides = [{
    browser: 'Google Chrome',
    link: 'https://support.google.com/chrome/answer/95647'
  }, {
    browser: 'Mozilla Firefox',
    link: 'https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer'
  }, {
    browser: 'Safari',
    link: 'https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac'
  }, {
    browser: 'Microsoft Edge',
    link: 'https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09'
  }, {
    browser: 'Opera',
    link: 'https://help.opera.com/en/latest/web-preferences/#cookies'
  }];
  const faqs = [{
    question: 'What are cookies?',
    answer: 'Cookies are small text files that websites place on your device (computer, smartphone, tablet) when you visit them. They help websites remember information about your visit, such as your preferred language, login status, and other settings.'
  }, {
    question: 'Why does GigShip use cookies?',
    answer: 'We use cookies to provide you with a better experience on our Platform. They help us keep you logged in, remember your preferences, understand how you use our services, improve our Platform, and show you relevant content and advertisements.'
  }, {
    question: 'Are cookies safe?',
    answer: 'Yes, cookies are generally safe. They cannot carry viruses or malware, and they cannot access other information on your device. However, some cookies can track your browsing activity across websites, which is why we give you control over non-essential cookies.'
  }, {
    question: 'Can I use GigShip without cookies?',
    answer: 'Essential cookies are required for the Platform to function properly (login, security, payment processing). You can disable non-essential cookies, but this may affect your experience and limit some features like personalization and analytics.'
  }, {
    question: 'How do I delete cookies?',
    answer: 'You can delete cookies through your browser settings at any time. However, deleting cookies may log you out of your account and reset your preferences. You\'ll need to log in again and reconfigure your settings.'
  }, {
    question: 'What happens if I disable cookies?',
    answer: 'Disabling all cookies will prevent you from using essential Platform features like logging in, making payments, or accessing your account. Disabling only non-essential cookies will still allow you to use the Platform but may reduce personalization and analytics.'
  }, {
    question: 'Do cookies contain personal information?',
    answer: 'Most cookies contain anonymous identifiers and don\'t directly contain personal information like your name or email. However, when combined with your account information, some cookies can be linked to you personally. We protect all cookie data with appropriate security measures.'
  }, {
    question: 'How long do cookies last?',
    answer: 'Cookie lifespans vary: Session cookies expire when you close your browser. Persistent cookies remain on your device for a set period (from a few hours to several years) or until you delete them. Each cookie type in our policy lists its typical retention period.'
  }, {
    question: 'Are third-party cookies the same as first-party cookies?',
    answer: 'First-party cookies are set directly by GigShip and used only on our Platform. Third-party cookies are set by external services we use (like analytics or advertising partners) and may track you across multiple websites. You can disable third-party cookies separately in your browser.'
  }, {
    question: 'Does GigShip respect Do Not Track signals?',
    answer: 'Currently, there is no industry consensus on how to respond to Do Not Track signals. We respect your cookie preferences through our cookie settings. You can manage your preferences at any time through your account settings or browser controls.'
  }];
  return <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-orange-600 to-red-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Cookie className="w-20 h-20 mx-auto mb-6" />
          <h1 className="text-5xl md:text-6xl font-extrabold mb-6">{t("static.cookiespolicypage.cookies_policy", {
            defaultValue: "Cookies Policy"
          })}</h1>
          <p className="text-xl md:text-2xl text-orange-100 mb-4 max-w-3xl mx-auto">
            {t("static.cookiespolicypage.how_we_use_cookies_and_similar_technolog", {
            defaultValue: "How we use cookies and similar technologies on GigShip"
          })}
          </p>
          <p className="text-lg text-orange-200 max-w-2xl mx-auto">
            <strong>{t("static.cookiespolicypage.effective_date", {
              defaultValue: "Effective Date:"
            })}</strong> {t("static.cookiespolicypage.november_1_2025", {
            defaultValue: "November 1, 2025 |"
          })} <strong>{t("static.cookiespolicypage.last_updated", {
              defaultValue: "Last Updated:"
            })}</strong> {t("static.cookiespolicypage.november_4_2025", {
            defaultValue: "November 4, 2025"
          })}
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg mb-8">
            <div className="flex items-start">
              <Info className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{t("static.cookiespolicypage.what_this_policy_covers", {
                  defaultValue: "What This Policy Covers"
                })}</h3>
                <p className="text-gray-700">
                  {t("static.cookiespolicypage.this_cookies_policy_explains_how_gigship", {
                  defaultValue: "This Cookies Policy explains how GigShip (operated by Cosbyte Technologies Limited) uses cookies and similar tracking technologies when you visit our Platform. This policy should be read alongside our Privacy Policy and Terms of Use."
                })}
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-6">{t("static.cookiespolicypage.what_are_cookies", {
            defaultValue: "What Are Cookies?"
          })}</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            {t("static.cookiespolicypage.cookies_are_small_text_files_stored_on_y", {
            defaultValue: "Cookies are small text files stored on your device (computer, smartphone, or tablet) when you visit a website. They are widely used to make websites work more efficiently and provide a better user experience. Cookies help websites remember your actions and preferences over time, so you don't have to re-enter information when you return."
          })}
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed">
            {t("static.cookiespolicypage.in_addition_to_cookies_we_may_use_simila", {
            defaultValue: "In addition to cookies, we may use similar technologies such as web beacons, pixels, and local storage to collect information about your use of our Platform."
          })}
          </p>
        </div>
      </section>

      {/* Types of Cookies */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t("static.cookiespolicypage.types_of_cookies_we_use", {
              defaultValue: "Types of Cookies We Use"
            })}</h2>
            <p className="text-xl text-gray-600">{t("static.cookiespolicypage.understanding_the_different_categories_o", {
              defaultValue: "Understanding the different categories of cookies"
            })}</p>
          </div>

          <div className="space-y-8">
            {cookieTypes.map((type, index) => {
            const {
              t
            } = useTranslation();
            const Icon = type.icon;
            return <div key={index} className={`bg-white border-2 ${type.borderColor} rounded-2xl overflow-hidden shadow-lg`}>
                  <div className={`${type.color} p-6`}>
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex items-center">
                        <Icon className="w-10 h-10 mr-4" />
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900">{type.name}</h3>
                          <p className="text-gray-700 mt-1">{type.description}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`inline-block px-4 py-1 rounded-full text-sm font-bold ${type.required ? 'bg-red-600 text-white' : 'bg-green-600 text-white'}`}>
                          {type.required ? 'Required' : 'Optional'}
                        </span>
                        <p className="text-sm text-gray-600 mt-1">{t("static.cookiespolicypage.retention", {
                        defaultValue: "Retention:"
                      })} {type.retention}</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-4">{t("static.cookiespolicypage.examples_of_use", {
                        defaultValue: "Examples of Use:"
                      })}</h4>
                        <ul className="space-y-2">
                          {type.examples.map((example, idx) => <li key={idx} className="flex items-start">
                              <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-700">{example}</span>
                            </li>)}
                        </ul>
                      </div>

                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-4">{t("static.cookiespolicypage.control_options", {
                        defaultValue: "Control Options:"
                      })}</h4>
                        {type.canDisable ? <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                            <div className="flex items-start">
                              <CheckCircle className="w-6 h-6 text-green-600 mr-3 flex-shrink-0" />
                              <div>
                                <p className="font-semibold text-gray-900 mb-2">{t("static.cookiespolicypage.you_can_disable_these_cookies", {
                              defaultValue: "You can disable these cookies"
                            })}</p>
                                <p className="text-sm text-gray-700">
                                  {t("static.cookiespolicypage.manage_these_cookies_through_our_cookie", {
                              defaultValue: "Manage these cookies through our cookie preference center or your browser settings without affecting core Platform functionality."
                            })}
                                </p>
                              </div>
                            </div>
                          </div> : <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                            <div className="flex items-start">
                              <AlertCircle className="w-6 h-6 text-red-600 mr-3 flex-shrink-0" />
                              <div>
                                <p className="font-semibold text-gray-900 mb-2">{t("static.cookiespolicypage.cannot_be_disabled", {
                              defaultValue: "Cannot be disabled"
                            })}</p>
                                <p className="text-sm text-gray-700">
                                  {t("static.cookiespolicypage.these_cookies_are_essential_for_the_plat", {
                              defaultValue: "These cookies are essential for the Platform to function. Disabling them will prevent you from using GigShip's services."
                            })}
                                </p>
                              </div>
                            </div>
                          </div>}
                      </div>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* Third-Party Cookies */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t("static.cookiespolicypage.third_party_cookies", {
              defaultValue: "Third-Party Cookies"
            })}</h2>
            <p className="text-xl text-gray-600">{t("static.cookiespolicypage.external_services_we_use_and_their_cooki", {
              defaultValue: "External services we use and their cookies"
            })}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {thirdPartyCookies.map((provider, index) => <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{provider.provider}</h3>
                <p className="text-gray-700 mb-4">{provider.purpose}</p>
                <div className="space-y-2 mb-4">
                  <div>
                    <span className="text-sm font-semibold text-gray-900">{t("static.cookiespolicypage.cookies", {
                    defaultValue: "Cookies:"
                  })} </span>
                    <span className="text-sm text-gray-600">{provider.cookies.join(', ')}</span>
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-gray-900">{t("static.cookiespolicypage.retention", {
                    defaultValue: "Retention:"
                  })} </span>
                    <span className="text-sm text-gray-600">{provider.retention}</span>
                  </div>
                </div>
                <a href={provider.link} target="_blank" rel="noopener noreferrer" className="text-blue-600 font-semibold hover:underline text-sm">
                  {t("static.cookiespolicypage.view_privacy_policy", {
                defaultValue: "View Privacy Policy \u2192"
              })}
                </a>
              </div>)}
          </div>
        </div>
      </section>

      {/* Cookie Management */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t("static.cookiespolicypage.managing_your_cookie_preferences", {
              defaultValue: "Managing Your Cookie Preferences"
            })}</h2>
            <p className="text-xl text-gray-600">{t("static.cookiespolicypage.you_have_control_over_how_cookies_are_us", {
              defaultValue: "You have control over how cookies are used"
            })}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {managementOptions.map((option, index) => {
            const Icon = option.icon;
            return <div key={index} className="bg-white rounded-xl p-6 shadow-lg">
                  <Icon className="w-10 h-10 text-blue-600 mb-4" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{option.title}</h3>
                  <p className="text-sm text-gray-600 mb-3">{option.description}</p>
                  <p className="text-xs text-gray-500 font-semibold">{option.action}</p>
                </div>;
          })}
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">{t("static.cookiespolicypage.browser_specific_cookie_guides", {
              defaultValue: "Browser-Specific Cookie Guides"
            })}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {browserGuides.map((guide, index) => <a key={index} href={guide.link} target="_blank" rel="noopener noreferrer" className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4 hover:border-blue-500 hover:shadow-md transition-all text-center">
                  <Globe className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <p className="font-semibold text-gray-900">{guide.browser}</p>
                  <p className="text-sm text-blue-600 mt-1">{t("static.cookiespolicypage.view_guide", {
                  defaultValue: "View Guide \u2192"
                })}</p>
                </a>)}
            </div>
          </div>
        </div>
      </section>

      {/* Important Notes */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">{t("static.cookiespolicypage.important_information", {
            defaultValue: "Important Information"
          })}</h2>
          
          <div className="space-y-6">
            <div className="bg-yellow-50 border-l-4 border-yellow-600 p-6 rounded-r-lg">
              <h3 className="text-lg font-bold text-gray-900 mb-2">{t("static.cookiespolicypage.impact_of_disabling_cookies", {
                defaultValue: "Impact of Disabling Cookies"
              })}</h3>
              <p className="text-gray-700">
                {t("static.cookiespolicypage.disabling_cookies_may_affect_your_abilit", {
                defaultValue: "Disabling cookies may affect your ability to use certain features of the Platform. Essential cookies are required for the Platform to function, and disabling them will prevent you from accessing your account or using our services."
              })}
              </p>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg">
              <h3 className="text-lg font-bold text-gray-900 mb-2">{t("static.cookiespolicypage.updates_to_this_policy", {
                defaultValue: "Updates to This Policy"
              })}</h3>
              <p className="text-gray-700">
                {t("static.cookiespolicypage.we_may_update_this_cookies_policy_from_t", {
                defaultValue: "We may update this Cookies Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. We will notify you of any significant changes by posting a notice on the Platform or sending you an email."
              })}
              </p>
            </div>

            <div className="bg-green-50 border-l-4 border-green-600 p-6 rounded-r-lg">
              <h3 className="text-lg font-bold text-gray-900 mb-2">{t("static.cookiespolicypage.your_consent", {
                defaultValue: "Your Consent"
              })}</h3>
              <p className="text-gray-700">
                {t("static.cookiespolicypage.by_continuing_to_use_gigship_you_consent", {
                defaultValue: "By continuing to use GigShip, you consent to our use of cookies as described in this policy. You can withdraw your consent at any time by adjusting your cookie preferences or browser settings."
              })}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t("static.cookiespolicypage.frequently_asked_questions", {
              defaultValue: "Frequently Asked Questions"
            })}</h2>
            <p className="text-xl text-gray-600">{t("static.cookiespolicypage.common_questions_about_cookies", {
              defaultValue: "Common questions about cookies"
            })}</p>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-xl p-6 border-l-4 border-orange-600 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-200 rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t("static.cookiespolicypage.questions_about_cookies", {
              defaultValue: "Questions About Cookies?"
            })}</h2>
            <p className="text-gray-700 mb-6">
              {t("static.cookiespolicypage.if_you_have_any_questions_about_our_use", {
              defaultValue: "If you have any questions about our use of cookies or this Cookies Policy, please contact us:"
            })}
            </p>
            <div className="space-y-2 mb-6">
              <p className="text-gray-700">
                <strong>{t("static.cookiespolicypage.email", {
                  defaultValue: "Email:"
                })}</strong>{' '}
                <a href="mailto:privacy@gigship.com" className="text-orange-600 font-semibold hover:underline">
                  {t("static.cookiespolicypage.privacy_gigship_com", {
                  defaultValue: "privacy@gigship.com"
                })}
                </a>
              </p>
              <p className="text-gray-700">
                <strong>{t("static.cookiespolicypage.phone", {
                  defaultValue: "Phone:"
                })}</strong> {t("static.cookiespolicypage.233_0_240_492_037", {
                defaultValue: "+233 (0)240 492 037"
              })}
              </p>
              <p className="text-gray-700">
                <strong>{t("static.cookiespolicypage.address", {
                  defaultValue: "Address:"
                })}</strong> {t("static.cookiespolicypage.p_o_box_lg1294_legon_accra_ghana", {
                defaultValue: "P. O. Box LG1294, Legon - Accra, Ghana"
              })}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://www.gigship.com/privacy" target="_blank" rel="noopener noreferrer" className="inline-block bg-orange-600 text-white px-6 py-3 rounded-full font-bold hover:bg-orange-700 transition">
                {t("static.cookiespolicypage.view_privacy_policy", {
                defaultValue: "View Privacy Policy"
              })}
              </a>
              <Link to="/support" className="inline-block bg-white text-orange-600 border-2 border-orange-600 px-6 py-3 rounded-full font-bold hover:bg-orange-50 transition">
                {t("static.cookiespolicypage.contact_support", {
                defaultValue: "Contact Support"
              })}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Note */}
      <div className="bg-gray-100 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-gray-600 mb-2">
            <strong>{t("static.cookiespolicypage.this_cookies_policy_is_effective_as_of_n", {
              defaultValue: "This Cookies Policy is effective as of November 1, 2025 and was last updated on November 4, 2025."
            })}</strong>
          </p>
          <p className="text-sm text-gray-600">
            <Link to="/" className="text-orange-600 hover:underline">{t("static.cookiespolicypage.back_to_gigship", {
              defaultValue: "\u2190 Back to GigShip"
            })}</Link>
          </p>
        </div>
      </div>
    </div>;
};
export default CookiesPolicyPage;